import { Component, DestroyRef, inject, OnInit, ViewChild } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormGroup } from "@angular/forms";
import { ActivatedRoute } from "@angular/router";
import { UiKitModule } from "@app/ui-kit.module";
import { ConfigurationWizardBackButtonComponent } from "@components/configuration-wizard-back-button/configuration-wizard-back-button.component";
import { ProductConfigStepperBarComponent } from "@components/stepper/product-config-stepper-bar/product-config-stepper-bar.component";
import { Family } from "@domain/product/monitoring/family/family";
import { FamilyStep, StepSelectionType } from "@domain/product/monitoring/family/family-step";
import { SubType } from "@domain/product/monitoring/family/family-step-option";
import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { LocalizeProductNamePipe } from "@domain/product/product";
import { PatientMonitorConfiguration } from "@domain/project/configurations/monitoring/patient-monitor-configuration";
import { MonitoringProject as Project } from "@domain/project/monitoring-project";
import { PatientMonitorSelectorType } from "@domain/project/monitoring-selector-types.enum";
import { WizardComponent } from "@odx/angular/components/wizard";
import { FloorplanMenuType } from "@project/floorplanner/floorplan-menu/fgds/floorplan-menu-type";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import {
  buildPatientMonitorGroupedSourceItems,
  PatientMonitorGroupedPartLabels,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts-source-item.factory";
import {
  getMonitoringOtherProductsLabel,
  getPatientMonitorGroupedPartLabels,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts.helper";
import { MonitoringGroupedPartsService } from "@project/monitoring/grouped-parts/monitoring-grouped-parts.service";
import {
  MonitoringGroupedPartSection,
  MonitoringGroupedPartSourceItem,
} from "@project/monitoring/grouped-parts/monitoring-grouped-parts.types";
import { MonitoringOverviewComponent } from "@project/monitoring/grouped-parts/monitoring-overview.component";
import { MonitoringConfigurationFactory } from "@project/monitoring/monitoring-configuration.factory";
import {
  createFamilyVariantStep,
  getConfigurationOptionIdentifier,
  getSelectedFamilyOption,
} from "@project/monitoring/monitoring-configuration.utils";
import { FamilyVariantMode, isPatientMonitorFamilySteps } from "@project/monitoring/monitoring-family-wizard.utils";
import {
  OptionDisplayWithQuantity,
  OptionSelectorComponent,
} from "@project/monitoring/option-device-selector/option-selector.component";
import { OptionDisplay } from "@project/monitoring/option-display.service";
import { ProductFamilySelectorComponent } from "@project/monitoring/product-family-selector/product-family-selector.component";
import { OriginalProductConfigurationFactory } from "@project/original-product-configuration-factory";
import { of, Subject, switchMap } from "rxjs";

@Component({
  selector: "app-patient-monitor-configuration",
  imports: [
    ConfigurationWizardBackButtonComponent,
    WizardComponent,
    UiKitModule,
    ProductFamilySelectorComponent,
    OptionSelectorComponent,
    ProductConfigStepperBarComponent,
    MonitoringOverviewComponent,
  ],
  providers: [OriginalProductConfigurationFactory, LocalizeProductNamePipe],
  templateUrl: "./patient-monitor-configuration.component.html",
  styleUrl: "./patient-monitor-configuration.component.scss",
})
export class PatientMonitorConfigurationComponent implements OnInit {
  protected PatientMonitorSelectorTitleKeyMap: Record<PatientMonitorSelectorType, string> = {
    [PatientMonitorSelectorType.FAMILY]: $localize`:@@patientMonitorConfiguration.step1.title:Select family`,
    [PatientMonitorSelectorType.VARIANT]: $localize`:@@patientMonitorConfiguration.variant.title:Select variant`,
    [PatientMonitorSelectorType.SENSOR]: $localize`:@@patientMonitorConfiguration.step2.title:Select sensor`,
    [PatientMonitorSelectorType.MOUNTING_GROUP]: $localize`:@@patientMonitorConfiguration.step3.title:Select mounting group`,
    [PatientMonitorSelectorType.MOUNTING_ARM]: $localize`:@@patientMonitorConfiguration.step4.title:Select mounting arm`,
    [PatientMonitorSelectorType.ATTACHMENTS]: $localize`:@@patientMonitorConfiguration.step5.title:Select attachments`,
    [PatientMonitorSelectorType.OVERVIEW]: $localize`:@@patientMonitorConfiguration.step6.title:Patient monitor configuration summary`,
  };

  protected readonly overviewCategoryLabels: PatientMonitorGroupedPartLabels = getPatientMonitorGroupedPartLabels();
  protected readonly overviewCategoryLabelMap: Partial<Record<PatientMonitorSelectorType, string>> = {
    [PatientMonitorSelectorType.FAMILY]: $localize`:@@patientMonitorConfiguration.overview.table.column1.category:Family`,
    [PatientMonitorSelectorType.SENSOR]: $localize`:@@patientMonitorConfiguration.overview.table.column2.category:Sensor`,
    [PatientMonitorSelectorType.MOUNTING_GROUP]: $localize`:@@patientMonitorConfiguration.overview.table.column3.category:Mounting Group`,
    [PatientMonitorSelectorType.MOUNTING_ARM]: $localize`:@@patientMonitorConfiguration.overview.table.column4.category:Mounting Arm`,
    [PatientMonitorSelectorType.ATTACHMENTS]: $localize`:@@patientMonitorConfiguration.overview.table.column5.category:Attachment`,
  };

  private activeRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private monitoringGroupedPartsService = inject(MonitoringGroupedPartsService);
  private readonly overviewItems$ = new Subject<MonitoringGroupedPartSourceItem[]>();

  protected project!: Project;
  protected originalConfiguration?: PatientMonitorConfiguration;
  protected activeStep = 0;
  private hasAppliedInitialOriginalConfigSkip = false;

  @ViewChild("configurationWizard")
  private configurationWizard!: WizardComponent;

  @ViewChild("backButton")
  private backButton!: ConfigurationWizardBackButtonComponent;

  protected selectedFamilyForm!: FormGroup;
  protected selectedFamilyVariantForm!: FormGroup;
  protected selectedSensorForm!: FormGroup;
  protected selectedMountingGroupForm!: FormGroup;
  protected selectedMountingArmForm!: FormGroup;

  protected family?: Family;
  protected familyVariantStep?: FamilyStep;
  protected sensorStep?: FamilyStep;
  protected mountingGroupStep?: FamilyStep;
  protected mountingArmStep?: FamilyStep;

  protected selectedFamily?: OptionDisplay;
  protected selectedFamilyVariant?: OptionDisplay;
  protected selectedSensor?: OptionDisplay;
  protected selectedMountingGroup?: OptionDisplay;
  protected selectedMountingArm?: OptionDisplay;
  protected selectedAttachments?: OptionDisplay[] = [];

  protected originalSensorOptions: MonitoringProductItem[] = [];
  protected originalFamilyVariantOptions: MonitoringProductItem[] = [];
  protected originalMountingGroupOptions: MonitoringProductItem[] = [];
  protected originalMountingArmOptions: MonitoringProductItem[] = [];

  private restoredSensorOptions: MonitoringProductItem[] = [];
  private restoredFamilyVariantOptions: MonitoringProductItem[] = [];
  private restoredMountingGroupOptions: MonitoringProductItem[] = [];
  private restoredMountingArmOptions: MonitoringProductItem[] = [];

  protected needsMountingArm = false;
  protected groupedSections: MonitoringGroupedPartSection[] = [];
  protected readonly otherProductsLabel = getMonitoringOtherProductsLabel();

  protected readonly MonitoringCategory = MonitoringCategory;
  protected readonly PatientMonitorSelectorType = PatientMonitorSelectorType;
  protected readonly FloorplanMenuType = FloorplanMenuType;

  constructor() {
    this.overviewItems$
      .pipe(
        switchMap((items) =>
          items.length
            ? this.monitoringGroupedPartsService.buildSections(
                items,
                MonitoringCategory.PATIENT_MONITORS,
                this.otherProductsLabel,
              )
            : of([]),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((sections) => {
        this.groupedSections = sections;
      });
  }

  ngOnInit(): void {
    this.project = this.activeRoute.parent!.snapshot.data["project"];
    if (this.activeRoute.snapshot.queryParams["config_id"]) {
      const configId = this.activeRoute.snapshot.queryParams["config_id"];
      this.originalConfiguration = this.project.patientMonitors.find((patientMonitor) => patientMonitor.id === configId);
      this.restoredFamilyVariantOptions = this.originalConfiguration?.family ? [this.originalConfiguration.family] : [];
      this.restoredSensorOptions = this.originalConfiguration?.sensor ? [this.originalConfiguration.sensor] : [];
      this.originalFamilyVariantOptions = [...this.restoredFamilyVariantOptions];
      this.restoredMountingGroupOptions = this.originalConfiguration?.mountingGroup
        ? [this.originalConfiguration.mountingGroup]
        : [];
      this.restoredMountingArmOptions = this.originalConfiguration?.mountingArm ? [this.originalConfiguration.mountingArm] : [];
      this.originalSensorOptions = [...this.restoredSensorOptions];
      this.originalMountingGroupOptions = [...this.restoredMountingGroupOptions];
      this.originalMountingArmOptions = [...this.restoredMountingArmOptions];
    }
  }

  protected setActiveStep(event: number) {
    if (event === 0 && this.originalConfiguration && !this.hasAppliedInitialOriginalConfigSkip) {
      this.hasAppliedInitialOriginalConfigSkip = true;
      this.configurationWizard.setActiveStepValid();
      this.configurationWizard.nextStep();
    } else {
      this.activeStep = event;
    }
  }

  protected setSelectedFamily($event: OptionDisplay | undefined) {
    this.selectedFamily = $event;
    this.family = $event?.family;
    const familyVariant = $event?.familyVariant?.variant;
    this.familyVariantStep =
      familyVariant?.mode === FamilyVariantMode.MULTIPLE_OPTIONS ? createFamilyVariantStep(familyVariant.step) : undefined;
    const normalizedSteps = isPatientMonitorFamilySteps($event?.familyVariant?.steps) ? $event.familyVariant.steps : undefined;
    this.sensorStep = normalizedSteps?.sensorStep ?? this.family?.steps?.[1];
    this.mountingGroupStep = this.ensureOptionalMountingGroupStep(normalizedSteps?.mountingGroupStep ?? this.family?.steps?.[2]);
    this.mountingArmStep = normalizedSteps?.mountingArmStep ?? this.family?.steps?.[3];
    this.selectedFamilyVariant = undefined;
    this.selectedSensor = undefined;
    this.selectedMountingGroup = undefined;
    this.selectedMountingArm = undefined;
    this.selectedAttachments = [];
    this.needsMountingArm = false;

    if (this.originalConfiguration && $event?.family?.familyNumber === this.originalConfiguration.familyNumber) {
      this.originalFamilyVariantOptions = [...this.restoredFamilyVariantOptions];
      this.originalSensorOptions = [...this.restoredSensorOptions];
      this.originalMountingGroupOptions = [...this.restoredMountingGroupOptions];
      this.originalMountingArmOptions = [...this.restoredMountingArmOptions];
    } else {
      this.originalFamilyVariantOptions = [];
      this.originalSensorOptions = [];
      this.originalMountingGroupOptions = [];
      this.originalMountingArmOptions = [];
    }
    this.overviewItems$.next([]);
  }

  protected setSelectedFamilyVariant($event: OptionDisplayWithQuantity[]) {
    this.selectedFamilyVariant = $event[0]?.optionDisplay;
    this.overviewItems$.next([]);
  }

  private ensureOptionalMountingGroupStep(step: FamilyStep | undefined): FamilyStep | undefined {
    if (!step) return undefined;
    if (step.stepSelectionType === StepSelectionType.PICK0TO1) return step;

    return new FamilyStep(step.options, StepSelectionType.PICK0TO1);
  }

  protected setSelectedSensor($event: OptionDisplayWithQuantity[]) {
    this.selectedSensor = $event[0]?.optionDisplay;
    this.selectedMountingGroup = undefined;
    this.selectedMountingArm = undefined;
    this.needsMountingArm = false;
    if (this.isOriginalFamilySelected() && this.isSameAsOriginalOption(this.selectedSensor, this.originalConfiguration?.sensor)) {
      this.originalMountingGroupOptions = [...this.restoredMountingGroupOptions];
      this.originalMountingArmOptions = [...this.restoredMountingArmOptions];
    } else {
      this.originalMountingGroupOptions = [];
      this.originalMountingArmOptions = [];
    }
    this.overviewItems$.next([]);
  }

  protected setSelectedMountingGroup($event: OptionDisplayWithQuantity[]) {
    const selectedMountingGroup = $event[0]?.optionDisplay;
    this.selectedMountingGroup = selectedMountingGroup;
    this.selectedMountingArm = undefined;
    if (
      this.isOriginalFamilySelected() &&
      this.isSameAsOriginalOption(this.selectedSensor, this.originalConfiguration?.sensor) &&
      this.isSameAsOriginalOption(selectedMountingGroup, this.originalConfiguration?.mountingGroup)
    ) {
      this.originalMountingArmOptions = [...this.restoredMountingArmOptions];
    } else {
      this.originalMountingArmOptions = [];
    }
    this.overviewItems$.next([]);
    if ((!this.family || this.hasMountingArmStep) && selectedMountingGroup?.option.subType === SubType.W) {
      this.needsMountingArm = true;
    } else {
      this.needsMountingArm = false;
    }
  }

  protected setSelectedMountingArm($event: OptionDisplayWithQuantity[]) {
    this.selectedMountingArm = $event[0]?.optionDisplay;
    this.overviewItems$.next([]);
  }

  protected onSubmit() {
    if (this.activeStep === 0) {
      this.selectedFamilyForm.markAllAsTouched();
      if (!this.selectedFamilyForm.valid) return;
    }

    if (this.activeStep === this.familyVariantStepIndex) {
      this.selectedFamilyVariantForm.markAllAsTouched();
      if (!this.selectedFamilyVariantForm.valid) return;
    }

    if (this.activeStep === this.sensorStepIndex) {
      this.selectedSensorForm.markAllAsTouched();
      if (!this.selectedSensorForm.valid) return;
    }

    if (this.activeStep === this.mountingGroupStepIndex) {
      this.selectedMountingGroupForm.markAllAsTouched();
      if (!this.selectedMountingGroupForm.valid) return;
      if (!this.needsMountingArm) {
        this.collectOverviewData();
        this.configurationWizard.setActiveStepValid();
        this.configurationWizard.nextStep();
        return;
      }
    }

    if (this.activeStep === this.mountingArmStepIndex && this.needsMountingArm) {
      this.selectedMountingArmForm.markAllAsTouched();
      if (!this.selectedMountingArmForm.valid) return;
      if (this.needsMountingArm) this.collectOverviewData();
    } else if (this.activeStep === this.summaryStepIndex) {
      if (this.originalConfiguration) {
        this.updatePatientMonitorConfig(this.originalConfiguration);
      } else {
        this.createNewPatientMonitorConfig();
      }
    }

    this.configurationWizard.setActiveStepValid();
    this.configurationWizard.nextStep();
  }

  protected onPrevious() {
    if (this.activeStep === this.summaryStepIndex && !this.needsMountingArm) {
      this.configurationWizard.previousStep();
    }
    this.configurationWizard.previousStep();
  }

  protected get familyVariantStepIndex(): number {
    return this.hasFamilyVariantStep ? 1 : -1;
  }

  protected get hasFamilyVariantStep(): boolean {
    return !!this.familyVariantStep?.options?.length;
  }

  protected get hasSensorStep(): boolean {
    return this.family ? this.hasOptions(this.sensorStep) : true;
  }

  protected get hasMountingGroupStep(): boolean {
    return this.family ? this.hasOptions(this.mountingGroupStep) : true;
  }

  protected get hasMountingArmStep(): boolean {
    return this.family ? this.hasOptions(this.mountingArmStep) : false;
  }

  protected get sensorStepIndex(): number {
    return this.hasSensorStep ? 1 + (this.hasFamilyVariantStep ? 1 : 0) : -1;
  }

  protected get mountingGroupStepIndex(): number {
    return this.hasMountingGroupStep ? 1 + (this.hasFamilyVariantStep ? 1 : 0) + (this.hasSensorStep ? 1 : 0) : -1;
  }

  protected get mountingArmStepIndex(): number {
    return this.hasMountingArmStep
      ? 1 + (this.hasFamilyVariantStep ? 1 : 0) + (this.hasSensorStep ? 1 : 0) + (this.hasMountingGroupStep ? 1 : 0)
      : -1;
  }

  protected get summaryStepIndex(): number {
    return (
      1 +
      (this.hasFamilyVariantStep ? 1 : 0) +
      (this.hasSensorStep ? 1 : 0) +
      (this.hasMountingGroupStep ? 1 : 0) +
      (this.needsMountingArm && this.hasMountingArmStep ? 1 : 0)
    );
  }

  protected get visibleActiveStep(): number {
    return this.activeStep;
  }

  protected get visibleStepCount(): number {
    return (
      1 +
      (this.hasFamilyVariantStep ? 1 : 0) +
      (this.hasSensorStep ? 1 : 0) +
      (this.hasMountingGroupStep ? 1 : 0) +
      (this.needsMountingArm && this.hasMountingArmStep ? 1 : 0) +
      1
    );
  }

  private hasOptions(step: FamilyStep | undefined): boolean {
    return !!step?.options?.length;
  }

  private collectOverviewData() {
    const selectedFamilyOption = getSelectedFamilyOption(this.selectedFamily, this.selectedFamilyVariant);
    const items: MonitoringGroupedPartSourceItem[] = buildPatientMonitorGroupedSourceItems(
      {
        family: selectedFamilyOption ? { option: selectedFamilyOption.option, quantity: 1 } : undefined,
        sensor: this.selectedSensor ? { option: this.selectedSensor.option, quantity: 1 } : undefined,
        mountingGroup: this.selectedMountingGroup ? { option: this.selectedMountingGroup.option, quantity: 1 } : undefined,
        mountingArm: this.selectedMountingArm ? { option: this.selectedMountingArm.option, quantity: 1 } : undefined,
        attachments: this.selectedAttachments?.map((attachment) => ({ option: attachment.option, quantity: 1 })),
      },
      this.overviewCategoryLabels,
    );

    if (!items.length) {
      this.overviewItems$.next([]);
      return;
    }

    this.overviewItems$.next(items);
  }

  private isOriginalFamilySelected(): boolean {
    return !!this.originalConfiguration && this.selectedFamily?.family?.familyNumber === this.originalConfiguration.familyNumber;
  }

  private isSameAsOriginalOption(option: OptionDisplay | undefined, original: MonitoringProductItem | undefined): boolean {
    if (!option || !original || !this.originalConfiguration) return false;

    return (
      getConfigurationOptionIdentifier(this.selectedFamily?.family?.familyName, option.option) ===
      getConfigurationOptionIdentifier(this.selectedFamily?.family?.familyName, original.option)
    );
  }

  private updatePatientMonitorConfig(config: PatientMonitorConfiguration) {
    const selectedFamilyOption = getSelectedFamilyOption(this.selectedFamily, this.selectedFamilyVariant);
    config.update({
      name: selectedFamilyOption?.displayName || this.family!.familyName,
      familyNumber: this.family!.familyNumber,
      family: selectedFamilyOption ? MonitoringProductItem.create(selectedFamilyOption.option, 1) : undefined,
      sensor: this.selectedSensor ? MonitoringProductItem.create(this.selectedSensor.option, 1) : undefined,
      mountingGroup: this.selectedMountingGroup ? MonitoringProductItem.create(this.selectedMountingGroup.option, 1) : undefined,
      mountingArm: this.selectedMountingArm ? MonitoringProductItem.create(this.selectedMountingArm.option, 1) : undefined,
      attachments: this.selectedAttachments?.map((attachment) => MonitoringProductItem.create(attachment.option, 1)),
    });
    this.backButton.navigateBack();
  }
  private createNewPatientMonitorConfig() {
    const selectedFamilyOption = getSelectedFamilyOption(this.selectedFamily, this.selectedFamilyVariant);
    const patientMonitorConfig = MonitoringConfigurationFactory.createPatientMonitor(
      selectedFamilyOption?.displayName || this.family!.familyName,
      this.family!,
      this.project,
      {
        family: selectedFamilyOption ? MonitoringProductItem.create(selectedFamilyOption.option, 1) : undefined,
        sensor: this.selectedSensor ? MonitoringProductItem.create(this.selectedSensor.option, 1) : undefined,
        mountingGroup: this.selectedMountingGroup
          ? MonitoringProductItem.create(this.selectedMountingGroup.option, 1)
          : undefined,
        mountingArm: this.selectedMountingArm ? MonitoringProductItem.create(this.selectedMountingArm.option, 1) : undefined,
        attachments: this.selectedAttachments?.map((attachment) => MonitoringProductItem.create(attachment.option, 1)),
      },
    );

    this.project.addPatientMonitors(patientMonitorConfig);
    this.backButton.navigateBack();
  }
}
